import { BaseService } from "../../core/baseService";
import { ApiResult } from "../../core/types";

export class OnlineCheckinAdminService extends BaseService {
  async inviteByReservation(
    providerReservationId: string,
  ): Promise<ApiResult<{ link: string; expiresAtUtc: string }>> {
    return this.handleRequest<{ link: string; expiresAtUtc: string }>(
      this.client.post("/api/admin/online-checkin/invite/by-reservation", {
        providerReservationId,
      }),
    );
  }

  async getSettings(): Promise<
    ApiResult<{
      settings: {
        timeZoneId: string;
        invitationLeadDays: number;
        invitationSendTimeLocal: string;
        reminderEnabled: boolean;
        reminderLeadDays: number;
        reminderSendTimeLocal?: string | null;
        documentVerificationMode?: string;
      };
      idenfyFeatureAvailable: boolean;
    }>
  > {
    return this.handleRequest(
      this.client.get("/api/admin/online-checkin/settings"),
    );
  }

  async saveSettings(payload: {
    timeZoneId: string;
    invitationLeadDays: number;
    invitationSendTimeLocal: string;
    reminderEnabled: boolean;
    reminderLeadDays: number;
    reminderSendTimeLocal?: string | null;
    documentVerificationMode?: string;
  }): Promise<ApiResult<{ message: string }>> {
    return this.handleRequest<{ message: string }>(
      this.client.put("/api/admin/online-checkin/settings", payload),
    );
  }

  async findSubmissionByReservation(
    providerReservationId: string,
  ): Promise<ApiResult<{ id: string; status?: string } | null>> {
    // Find any non-draft submission for this reservation (Submitted/Verified/etc.)
    const res = await this.handleRequest<{
      items: Array<{ id: string; status?: string }>;
    }>(
      this.client.get(`/api/admin/online-checkin/submissions`, {
        params: { page: 1, pageSize: 1, reservationId: providerReservationId },
      }),
    );
    if (!res.success) return { success: false, error: res.error };
    const item = res.data.items?.[0];
    if (!item?.id) return { success: true, data: null };
    if (item.status && String(item.status).toLowerCase() === "draft")
      return { success: true, data: null };
    return { success: true, data: { id: item.id, status: item.status } };
  }

  async findSubmissionByStay(
    stayId: string,
  ): Promise<ApiResult<{ id: string; status?: string } | null>> {
    // Find any non-draft submission for the unified StayId
    const res = await this.handleRequest<{
      items: Array<{ id: string; status?: string }>;
    }>(
      this.client.get(`/api/admin/online-checkin/submissions`, {
        params: { page: 1, pageSize: 1, stayId },
      }),
    );
    if (!res.success) return { success: false, error: res.error };
    const item = res.data.items?.[0];
    if (!item?.id) return { success: true, data: null };
    if (item.status && String(item.status).toLowerCase() === "draft")
      return { success: true, data: null };
    return { success: true, data: { id: item.id, status: item.status } };
  }

  async getSubmission(submissionId: string): Promise<ApiResult<Record<string, unknown>>> {
    return this.handleRequest<Record<string, unknown>>(
      this.client.get(`/api/admin/online-checkin/submissions/${submissionId}`),
    );
  }

  async getDocumentSas(
    submissionId: string,
    side: "front" | "back",
    minutes = 10,
    guestIndex?: number,
  ): Promise<ApiResult<{ url: string }>> {
    const params: Record<string, string | number> = { side, minutes };
    if (guestIndex !== undefined) params.guestIndex = guestIndex;
    return this.handleRequest<{ url: string }>(
      this.client.get(
        `/api/admin/online-checkin/submissions/${submissionId}/document-sas`,
        { params },
      ),
    );
  }

  async getVerificationDetails(
    submissionId: string,
    guestIndex?: number,
  ): Promise<ApiResult<{
    status: string;
    fraudStatus?: string;
    manuallyVerified?: boolean;
    extractedData?: {
      firstName?: string;
      lastName?: string;
      dateOfBirth?: string;
      documentNumber?: string;
      nationality?: string;
      documentType?: string;
      documentExpiry?: string;
    };
  }>> {
    const params: Record<string, number> = {};
    if (guestIndex !== undefined) params.guestIndex = guestIndex;
    return this.handleRequest(
      this.client.get(
        `/api/admin/online-checkin/submissions/${submissionId}/verification`,
        { params },
      ),
    );
  }

  async manuallyVerify(
    submissionId: string,
    guestIndex?: number,
  ): Promise<ApiResult<void>> {
    const params: Record<string, number> = {};
    if (guestIndex !== undefined) params.guestIndex = guestIndex;
    return this.handleRequest<void>(
      this.client.post(
        `/api/admin/online-checkin/submissions/${submissionId}/manual-verify`,
        null,
        { params },
      ),
    );
  }
}
